import React, { Suspense, useEffect, useRef } from 'react';
import { ProtectedRoute } from './components/ProtectedRoute';
import { LoadingDashboard } from './components/LoadingDashboard';
import { useTranslation } from 'react-i18next';
import { FeedbackWidget } from './components/feedback/FeedbackWidget';
import { Footer } from './components/Footer';

const StudentPage = React.lazy(() =>
  import('./components/StudentPage').then(module => ({ default: module.StudentPage }))
);
const ProfessorPage = React.lazy(() =>
  import('./components/ProfessorPage').then(module => ({ default: module.ProfessorPage }))
);
const SysAdminPage = React.lazy(() =>
  import('./components/SysAdminPage').then(module => ({ default: module.SysAdminPage }))
);
const InstituteAdminPage = React.lazy(() =>
  import('./components/InstituteAdminPage').then(module => ({ default: module.InstituteAdminPage }))
);

function App({ user, onUpdateUser }) {
  const { t } = useTranslation();
  // Capture when the app starts rendering so we can log first dashboard render time
  const loadStartRef = useRef(performance.now());
  const hasLoggedRef = useRef(false);
  // Verify user is authenticated
  if (!user) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100">
        <div className="bg-white p-8 rounded-lg shadow-md text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">{t('auth.authRequiredTitle')}</h2>
          <p className="text-gray-600">{t('auth.authRequiredMessage')}</p>
        </div>
      </div>
    );
  }

  // Normalize role to lowercase for comparison
  const userRole = user?.role?.toLowerCase();

  const PageLoadLogger = ({ role }) => {
    useEffect(() => {
      if (hasLoggedRef.current) return;
      const duration = Math.round(performance.now() - loadStartRef.current);
      console.info('[perf] dashboard first paint', { role, durationMs: duration });
      hasLoggedRef.current = true;
    }, [role]);
    return null;
  };

  // Route based on user role
  if (userRole === 'student') {
    return (
      <div className="min-h-screen flex flex-col">
        <Suspense fallback={<LoadingDashboard />}>
          <ProtectedRoute user={user} requiredRole="Student">
            <PageLoadLogger role="student" />
            <StudentPage user={user} onUpdateUser={onUpdateUser} />
            <FeedbackWidget user={user} />
          </ProtectedRoute>
        </Suspense>
        <Footer />
      </div>
    );
  } else if (userRole === 'professor') {
    return (
      <div className="min-h-screen flex flex-col">
        <Suspense fallback={<LoadingDashboard />}>
          <ProtectedRoute user={user} requiredRole="Professor">
            <PageLoadLogger role="professor" />
            <ProfessorPage user={user} onUpdateUser={onUpdateUser} />
            <FeedbackWidget user={user} />
          </ProtectedRoute>
        </Suspense>
        <Footer />
      </div>
    );
  } else if (userRole === 'sysadmin') {
    return (
      <div className="min-h-screen flex flex-col">
        <Suspense fallback={<LoadingDashboard />}>
          <ProtectedRoute user={user} requiredRole="SysAdmin">
            <PageLoadLogger role="sysadmin" />
            <SysAdminPage user={user} onUpdateUser={onUpdateUser} />
            <FeedbackWidget user={user} />
          </ProtectedRoute>
        </Suspense>
        <Footer />
      </div>
    );
  } else if (userRole === 'instituteadmin') {
    return (
      <div className="min-h-screen flex flex-col">
        <Suspense fallback={<LoadingDashboard />}>
          <ProtectedRoute user={user} requiredRole="InstituteAdmin">
            <PageLoadLogger role="instituteadmin" />
            <InstituteAdminPage user={user} onUpdateUser={onUpdateUser} />
            <FeedbackWidget user={user} />
          </ProtectedRoute>
        </Suspense>
        <Footer />
      </div>
    );
  } else {
    // Handle unknown roles
    return (
      <div className="min-h-screen flex flex-col">
        <div className="flex-1 flex items-center justify-center bg-gray-100">
          <div className="bg-white p-8 rounded-lg shadow-md text-center">
            <h2 className="text-2xl font-bold text-red-600 mb-4">{t('auth.invalidRole')}</h2>
            <p className="text-gray-600">{t('auth.unrecognizedRole', { role: user?.role })}</p>
            <p className="text-sm text-gray-500 mt-2">{t('auth.contactAdmin')}</p>
          </div>
        </div>
        <Footer />
      </div>
    );
  }
}

export default App;
